import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import DragIndicator from '@mui/icons-material/DragIndicator'
import Lock from '@mui/icons-material/Lock'
import LockOpen from '@mui/icons-material/LockOpen'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { move, reduce, update } from 'ramda'
import React from 'react'

import Tooltip from '@/app/component/atom/tooltip'
import { Column } from '@/config/types'

type Props = {
  headers: Column[]
  setHeaders: (headers: Column[]) => void
}

export default function ConfigureTable({ headers, setHeaders }: Props) {
  const [isConfigureListOpen, setIsConfigureListOpen] = React.useState<null | HTMLElement>(null)

  const handleToggleVisibility = (index: number) => {
    setHeaders(
      update(
        index,
        {
          ...headers[index],
          visible: !headers[index].visible,
        },
        headers,
      ),
    )
  }

  const handleToggleSticky = (index: number) => {
    setHeaders(
      update(
        index,
        {
          ...headers[index],
          sticky: headers[index].sticky === 'left' ? undefined : 'left',
        },
        headers,
      ),
    )
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return
    }
    setHeaders(move(result.source.index, result.destination.index, headers))
  }

  const visibleCount = reduce((accum: number, elem: Column) => accum + (elem.visible ? 1 : 0), 0, headers)

  return (
    <div>
      <Tooltip title="Manage table columns">
        <IconButton
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => setIsConfigureListOpen(event.currentTarget)}
        >
          <Icon>settings</Icon>
        </IconButton>
      </Tooltip>
      <Menu
        id="configure-menu"
        anchorEl={isConfigureListOpen}
        open={Boolean(isConfigureListOpen)}
        onClose={() => setIsConfigureListOpen(null)}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="configure-table-columns">
            {(droppableProvided) => (
              <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                {headers.map((header: Column, index: number) => (
                  <Draggable key={header.key} draggableId={header.key} index={index}>
                    {(draggableProvided) => (
                      <MenuItem
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        disabled={visibleCount < 3 && header.visible}
                        style={{ ...draggableProvided.draggableProps.style }}
                      >
                        <div
                          {...draggableProvided.dragHandleProps}
                          style={{ display: 'flex', alignItems: 'center', outline: 'none' }}
                        >
                          <Tooltip title="Drag/ Drop">
                            <DragIndicator
                              style={{
                                fontSize: '1.2rem',
                                marginRight: '10px',
                                marginLeft: '-10px',
                              }}
                            />
                          </Tooltip>
                        </div>
                        <CustomCheckbox
                          tooltip="Hide/ Unhide"
                          disabled={visibleCount < 3 && header.visible}
                          checked={header.visible}
                          icon={VisibilityOff}
                          checkedIcon={Visibility}
                          onToggle={() => handleToggleVisibility(index)}
                        />
                        <CustomCheckbox
                          tooltip="Lock/ Unlock"
                          checked={header.sticky === 'left'}
                          icon={LockOpen}
                          checkedIcon={Lock}
                          onToggle={() => handleToggleSticky(index)}
                        />
                        <Typography>{header.label}</Typography>
                      </MenuItem>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Menu>
    </div>
  )
}

type CustomCheckboxProps = {
  checked: boolean
  checkedIcon: typeof Lock
  disabled?: boolean
  icon: typeof Lock
  tooltip?: string

  onToggle: () => void
}

const CustomCheckbox = ({ checked, checkedIcon, disabled, icon, tooltip = '', onToggle }: CustomCheckboxProps) => {
  const ActiveIcon = checked ? checkedIcon : icon
  return (
    <Tooltip title={tooltip}>
      <div
        onClick={disabled ? () => {} : onToggle}
        style={{
          cursor: 'pointer',
          paddingRight: '4px',
        }}
      >
        <ActiveIcon
          style={{
            fontSize: '0.8rem',
            opacity: '0.7',
            color: checked ? '#205978' : 'rgba(0, 0, 0, 0.54)',
          }}
        />
      </div>
    </Tooltip>
  )
}
